import { useState } from 'react';
import { REGIONS } from '../data/regions';
import { ROUTES } from '../data/routes';
import { LANDMARKS } from '../data/landmarks';
import type { MapEntity, DoseLevel, LandmarkKind } from '../data/types';
import { useZoomPan } from '../hooks/useZoomPan';
import styles from './MapView.module.css';

interface MapViewProps {
  selected: MapEntity | null;
  visited: Set<string>;
  onSelect: (entity: MapEntity | null) => void;
}

type RouteFilter = 'none' | DoseLevel | 'all';

const ROUTE_FILTERS: { value: RouteFilter; label: string }[] = [
  { value: 'none', label: 'no routes' },
  { value: 'bedroom', label: 'bedroom' },
  { value: 'relationship', label: 'relationship' },
  { value: 'lifestyle', label: 'lifestyle' },
  { value: 'tpe', label: 'tpe' },
  { value: 'all', label: 'all' },
];

/** Landmark glyph by kind. Different shapes so the visitor can tell
 *  a practice from a ritual from a term at a glance. */
function LandmarkGlyph({
  kind,
  cx,
  cy,
  selected,
  visited,
}: {
  kind: LandmarkKind;
  cx: number;
  cy: number;
  selected: boolean;
  visited: boolean;
}) {
  const r = selected ? 9 : 6.5;
  const fill = '#6B0F1A';
  const stroke = '#3D2914';
  const strokeWidth = 1.5;

  if (kind === 'practice') {
    return <circle cx={cx} cy={cy} r={r} fill={fill} stroke={stroke} strokeWidth={strokeWidth} />;
  }
  if (kind === 'ritual') {
    // Diamond (rotated square) for ritual landmarks.
    const d = r + 1.5; // diamond reads slightly smaller, bump it up
    const points = `${cx},${cy - d} ${cx + d},${cy} ${cx},${cy + d} ${cx - d},${cy}`;
    return <polygon points={points} fill={fill} stroke={stroke} strokeWidth={strokeWidth} />;
  }
  // term — small upward triangle
  const t = r + 1;
  const points = `${cx},${cy - t} ${cx + t},${cy + t * 0.85} ${cx - t},${cy + t * 0.85}`;
  return (
    <>
      <polygon points={points} fill={fill} stroke={stroke} strokeWidth={strokeWidth} />
      {visited && (
        <circle cx={cx} cy={cy} r={r + 5} fill="none" stroke={stroke} strokeWidth={0.5} strokeDasharray="2 2" />
      )}
    </>
  );
}

export function MapView({ selected, visited, onSelect }: MapViewProps) {
  const { view, containerRef, bind, reset, zoomIn, zoomOut } = useZoomPan();
  const [routeFilter, setRouteFilter] = useState<RouteFilter>('none');
  const [hoveredLandmark, setHoveredLandmark] = useState<string | null>(null);

  function isSelected(entity: MapEntity): boolean {
    if (!selected) return false;
    return selected.type === entity.type && selected.id === entity.id;
  }

  const visibleRoutes = ROUTES.filter((r) => {
    if (routeFilter === 'all') return true;
    if (routeFilter === 'none') return false;
    return r.doseLevel === routeFilter;
  });

  return (
    <div className={styles.frame}>
      {/* Top map ribbon: route filter on the left, legend on the right. */}
      <div className={styles.ribbon}>
        <div className={styles.routeFilter} role="group" aria-label="show routes by dose level">
          <span className={styles.ribbonLabel}>routes</span>
          {ROUTE_FILTERS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              className={`${styles.chip} ${routeFilter === opt.value ? styles.chipActive : ''}`}
              onClick={() => setRouteFilter(opt.value)}
              aria-pressed={routeFilter === opt.value}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <div className={styles.legend} aria-label="map legend">
          <span className={styles.legendItem}>
            <svg width="16" height="14" viewBox="-8 -7 16 14" aria-hidden="true">
              <circle r="5" cx="0" cy="0" fill="#6B0F1A" stroke="#3D2914" strokeWidth="1.5" />
            </svg>
            practice
          </span>
          <span className={styles.legendItem}>
            <svg width="16" height="14" viewBox="-8 -7 16 14" aria-hidden="true">
              <polygon points="0,-6 6,0 0,6 -6,0" fill="#6B0F1A" stroke="#3D2914" strokeWidth="1.5" />
            </svg>
            ritual
          </span>
          <span className={styles.legendItem}>
            <svg width="16" height="14" viewBox="-8 -7 16 14" aria-hidden="true">
              <polygon points="0,-6 6,5 -6,5" fill="#6B0F1A" stroke="#3D2914" strokeWidth="1.5" />
            </svg>
            term
          </span>
        </div>
      </div>

      <div
        ref={containerRef}
        className={styles.viewport}
        onPointerDown={bind.onPointerDown}
        onPointerMove={bind.onPointerMove}
        onPointerUp={bind.onPointerUp}
        onPointerCancel={bind.onPointerUp}
      >
        <div
          className={styles.canvas}
          style={{ transform: `translate(${view.tx}px, ${view.ty}px) scale(${view.scale})` }}
        >
          <svg
            viewBox="0 0 1000 1000"
            xmlns="http://www.w3.org/2000/svg"
            className={styles.svg}
          >
            <defs>
              <pattern id="hnn-parchment" patternUnits="userSpaceOnUse" width="80" height="80">
                <rect width="80" height="80" fill="#F5E6D3" />
                <circle cx="20" cy="30" r="0.6" fill="#3D2914" opacity="0.06" />
                <circle cx="55" cy="60" r="0.4" fill="#3D2914" opacity="0.04" />
                <circle cx="70" cy="15" r="0.5" fill="#3D2914" opacity="0.05" />
              </pattern>
              <radialGradient id="hnn-vignette" cx="50%" cy="50%" r="55%">
                <stop offset="60%" stopColor="#000" stopOpacity="0" />
                <stop offset="100%" stopColor="#3D2914" stopOpacity="0.35" />
              </radialGradient>
            </defs>

            {/* parchment + vignette */}
            <rect width="1000" height="1000" fill="url(#hnn-parchment)" />
            <rect width="1000" height="1000" fill="url(#hnn-vignette)" />

            {/* compass rose, tucked into the top-left */}
            <g transform="translate(80, 80)" opacity="0.6">
              <circle cx="0" cy="0" r="34" fill="none" stroke="#3D2914" strokeWidth="1" />
              <circle cx="0" cy="0" r="20" fill="none" stroke="#3D2914" strokeWidth="0.5" />
              <line x1="0" y1="-30" x2="0" y2="30" stroke="#3D2914" strokeWidth="1" />
              <line x1="-30" y1="0" x2="30" y2="0" stroke="#3D2914" strokeWidth="1" />
              <polygon points="0,-30 -4,-20 4,-20" fill="#6B0F1A" />
              <text x="0" y="-38" textAnchor="middle" className={styles.compassLabel} fill="#3D2914">
                N
              </text>
            </g>

            {/* title cartouche, centered along the top */}
            <g transform="translate(500, 56)">
              <text textAnchor="middle" className={styles.title} fill="#3D2914">
                the atlas of the inner country
              </text>
            </g>

            {/* routes — drawn before regions so they sit under the wash */}
            <g className={styles.routes}>
              {visibleRoutes.map((route) => {
                const sel = isSelected({ type: 'route', id: route.id });
                return (
                  <path
                    key={route.id}
                    d={route.d}
                    fill="none"
                    stroke="#6B0F1A"
                    strokeOpacity={sel ? 0.95 : 0.55}
                    strokeWidth={(route.strokeWidth ?? 2) + (sel ? 1.5 : 0)}
                    strokeDasharray={route.dashArray}
                    strokeLinecap="round"
                    className={styles.route}
                    onClick={(e) => {
                      e.stopPropagation();
                      onSelect({ type: 'route', id: route.id });
                    }}
                  />
                );
              })}
            </g>

            {/* regions */}
            <g>
              {REGIONS.map((region) => {
                const selectedThis = isSelected({ type: 'region', id: region.id });
                // Place the label above the region so it doesn't collide
                // with the landmark pins inside the wash circle.
                const labelY = region.cy - 78;
                return (
                  <g
                    key={region.id}
                    className={styles.regionGroup}
                    onClick={(e) => {
                      e.stopPropagation();
                      onSelect({ type: 'region', id: region.id });
                    }}
                  >
                    {/* atmospheric tint */}
                    <circle
                      cx={region.cx}
                      cy={region.cy}
                      r={105}
                      fill={region.accent}
                      opacity={selectedThis ? 0.4 : 0.22}
                      className={styles.regionWash}
                    />
                    {/* outline */}
                    <circle
                      cx={region.cx}
                      cy={region.cy}
                      r={88}
                      fill="none"
                      stroke="#3D2914"
                      strokeWidth={selectedThis ? 2 : 1.5}
                      strokeDasharray={selectedThis ? '' : '3 5'}
                    />
                    {/* label — rendered last so it sits on top */}
                    <text
                      x={region.cx}
                      y={labelY}
                      textAnchor="middle"
                      className={styles.regionLabel}
                      fill="#3D2914"
                    >
                      {region.name}
                    </text>
                  </g>
                );
              })}
            </g>

            {/* landmarks */}
            <g>
              {LANDMARKS.map((lm) => {
                const selectedThis = isSelected({ type: 'landmark', id: lm.id });
                const isHovered = hoveredLandmark === lm.id;
                const showLabel = selectedThis || isHovered;
                const isVisited = visited.has(`landmark:${lm.id}`);
                return (
                  <g
                    key={lm.id}
                    className={styles.landmarkGroup}
                    onClick={(e) => {
                      e.stopPropagation();
                      onSelect({ type: 'landmark', id: lm.id });
                    }}
                    onPointerEnter={() => setHoveredLandmark(lm.id)}
                    onPointerLeave={() =>
                      setHoveredLandmark((cur) => (cur === lm.id ? null : cur))
                    }
                  >
                    {/* Generous invisible hit target so small glyphs are
                     *  easy to click without overlapping each other. */}
                    <circle cx={lm.cx} cy={lm.cy} r={16} fill="transparent" />
                    {isVisited && (
                      <circle
                        cx={lm.cx}
                        cy={lm.cy}
                        r={selectedThis ? 14 : 11}
                        fill="none"
                        stroke="#3D2914"
                        strokeWidth={0.6}
                        strokeDasharray="2 2"
                      />
                    )}
                    <LandmarkGlyph
                      kind={lm.kind}
                      cx={lm.cx}
                      cy={lm.cy}
                      selected={selectedThis}
                      visited={isVisited}
                    />
                    {showLabel && (
                      <g pointerEvents="none">
                        {/* label backplate to make the name legible
                         *  even when it crosses route lines */}
                        <rect
                          x={lm.cx - lm.name.length * 3.4}
                          y={lm.cy + 14}
                          width={lm.name.length * 6.8}
                          height={16}
                          rx={3}
                          fill="#F8EBD0"
                          opacity={0.92}
                        />
                        <text
                          x={lm.cx}
                          y={lm.cy + 26}
                          textAnchor="middle"
                          className={styles.landmarkLabel}
                          fill="#3D2914"
                        >
                          {lm.name}
                        </text>
                      </g>
                    )}
                  </g>
                );
              })}
            </g>
          </svg>
        </div>
      </div>

      {/* zoom controls */}
      <div className={styles.controls}>
        <button type="button" onClick={zoomIn} aria-label="zoom in">
          +
        </button>
        <button type="button" onClick={zoomOut} aria-label="zoom out">
          −
        </button>
        <button type="button" onClick={reset} aria-label="reset view">
          ⌂
        </button>
      </div>
    </div>
  );
}
